import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { Link } from "@tanstack/react-router";
import {
  AdLandingHero,
  QuickQuoteForm,
  ReviewCards,
  SettlementSteps,
  TrustAndOffices,
  WhatsIncluded,
} from "@/components/ad-landing";
import { FaqList } from "@/components/page-parts";
import { faqSchema, localBusinessSchema } from "@/lib/firm";
import { SUNSHINE_COAST_HUB } from "@/lib/locations/sunshine-coast-hub";
import { LocalConveyancingDepth } from "@/components/local-conveyancing-depth";
import { SUNSHINE_COAST_DEPTH } from "@/lib/local-conveyancing-depth";

export const Route = createFileRoute("/conveyancing-sunshine-coast")({
  head: () => ({
    meta: [
      { title: fitTitle(SUNSHINE_COAST_HUB.metaTitle) },
      { name: "description", content: fitDescription(SUNSHINE_COAST_HUB.description) },
      { property: "og:title", content: fitTitle(SUNSHINE_COAST_HUB.metaTitle) },
      { property: "og:description", content: fitDescription(SUNSHINE_COAST_HUB.description) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/conveyancing-sunshine-coast" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/conveyancing-sunshine-coast" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema([...SUNSHINE_COAST_HUB.faqs, ...SUNSHINE_COAST_DEPTH.extraFaqs])) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessSchema) },
    ],
  }),
  component: SunshineCoastLanding,
});

const C = SUNSHINE_COAST_HUB;

function SunshineCoastLanding() {
  return (
    <>
      <AdLandingHero
        eyebrow="Conveyancing solicitors — Sunshine Coast"
        title={C.title}
        lead={C.lead}
        suburb="Sunshine Coast"
      />
      <ReviewCards />
      <WhatsIncluded />
      <SettlementSteps />
      <section className="mx-auto max-w-4xl px-5 py-10">
        <h2 className="font-serif text-2xl text-foreground">Before you sign, send us the contract</h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Tell us the property address, whether you are buying or selling and whether anything
          has already been signed. Include any finance, inspection or settlement deadline.
          A Kings Beach apartment, a Pelican Waters waterfront home and a hinterland block
          raise different questions; we can discuss the checks relevant to your plans.
        </p>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Use the <a href="#enquiry-bottom" className="font-semibold text-primary underline">on-page quote form</a>
          {" "}for a written quote. Compare the professional fee separately from searches,
          electronic settlement charges, registration and transfer duty. Our{" "}
          <Link to="/conveyancing-cost-qld" className="font-semibold text-primary underline">Queensland conveyancing cost guide</Link>
          {" "}explains the difference.
        </p>
      </section>
      <LocalConveyancingDepth d={SUNSHINE_COAST_DEPTH} />
      <section className="mx-auto max-w-4xl px-5 pb-12">
        <h2 className="font-serif text-2xl text-foreground">Buying or selling in Caloundra?</h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Our Golden Beach office handles contracts across the region. For Kings Beach units,
          older coastal homes and Pelican Waters canal properties, see our{" "}
          <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">Caloundra conveyancing advice and fixed fees</Link>.
        </p>
      </section>
      <section className="mx-auto max-w-4xl px-5 pb-12">
        <h2 className="font-serif text-2xl text-foreground">Conveyancing across the coast and hinterland</h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">An apartment, an established house and an unregistered lot need different enquiries. We act from Golden Beach; these local pages explain the issues to discuss before you sign.</p>
        <ul className="mt-5 grid gap-3 text-base sm:grid-cols-2">
          <li><Link to="/mooloolaba" className="text-primary underline">Mooloolaba apartments and waterfront property</Link></li>
          <li><Link to="/maroochydore" className="text-primary underline">Maroochydore homes and off-the-plan apartments</Link></li>
          <li><Link to="/buderim" className="text-primary underline">Buderim homes, renovations and downsizing</Link></li>
          <li><Link to="/noosa" className="text-primary underline">Noosa property and holiday-letting enquiries</Link></li>
          <li><Link to="/wurtulla" className="text-primary underline">Wurtulla homes, duplexes and ownership types</Link></li>
          <li><Link to="/conveyancing/$suburb" params={{ suburb: "coolum" }} className="text-primary underline">Coolum homes and apartments</Link></li>
          <li><Link to="/conveyancing/$suburb" params={{ suburb: "nambour" }} className="text-primary underline">Nambour property purchases and sales</Link></li>
          <li><Link to="/conveyancing/$suburb" params={{ suburb: "sunshine-coast-hinterland" }} className="text-primary underline">Maleny, Montville and hinterland property</Link></li>
          <li><Link to="/conveyancing/$suburb" params={{ suburb: "currimundi" }} className="text-primary underline">Currimundi homes and townhouses</Link></li>
          <li><Link to="/conveyancing/$suburb" params={{ suburb: "baringa" }} className="text-primary underline">Baringa established-home purchases and resales</Link></li>
          <li><Link to="/conveyancing/$suburb" params={{ suburb: "aura" }} className="text-primary underline">Aura new lots and house-and-land contracts</Link></li>
        </ul>
      </section>
      <section className="mx-auto max-w-6xl px-5 pb-12">
        <h2 className="font-serif text-3xl text-foreground">What we watch for on the Sunshine Coast</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {C.localNotes.slice(0, 2).map((n) => (
            <div key={n.t} className="rounded-md border border-border bg-card p-5">
              <h3 className="font-serif text-lg text-foreground">{n.t}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{n.d}</p>
            </div>
          ))}
        </div>
      </section>
      <TrustAndOffices />
      <section className="mx-auto max-w-6xl px-5 pb-12">
        <h2 className="font-serif text-3xl text-foreground">Sunshine Coast conveyancing questions</h2>
        <div className="mt-6 max-w-3xl"><FaqList items={C.faqs.slice(0, 4)} /></div>
      </section>
      <section className="mx-auto max-w-xl px-5 pb-12">
        <QuickQuoteForm id="enquiry-bottom" suburb="Sunshine Coast" heading="Ready? Get your fixed Sunshine Coast quote" />
      </section>
      <section className="mx-auto max-w-3xl px-5 pb-24">
        <details className="rounded-md border border-border bg-card p-6">
          <summary className="cursor-pointer font-serif text-xl text-foreground">More about conveyancing on the Sunshine Coast</summary>
          {C.paragraphs.map((p) => (
            <p key={p} className="mt-5 text-base leading-relaxed text-muted-foreground">{p}</p>
          ))}
          {C.localNotes.slice(2).map((n) => (
            <div key={n.t} className="mt-5">
              <h3 className="font-serif text-lg text-foreground">{n.t}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{n.d}</p>
            </div>
          ))}
          {C.faqs.length > 4 && <div className="mt-8"><FaqList items={C.faqs.slice(4)} /></div>}
          <h3 className="mt-8 font-serif text-2xl text-foreground">Local purchases — hypothetical examples</h3>
          <p className="mt-3 text-sm text-muted-foreground">These illustrations are invented, not Coastside Law client matters.</p>
          <ul className="mt-4 list-disc space-y-4 pl-5 text-base leading-relaxed text-muted-foreground">
            <li><strong>Kings Beach apartment:</strong> a buyer hopes to use the unit for holiday letting. The contract, body corporate records and relevant planning and use requirements need checking before relying on that income.</li>
            <li><strong>Pelican Waters canal home:</strong> a buyer plans to replace the pontoon. Existing title rights do not establish approval for new works; the proposed changes, applicable approvals and maintenance obligations need separate enquiries.</li>
            <li><strong>Nambour-area acreage:</strong> a buyer expects to add a second dwelling. Access, services, on-site sewerage and planning requirements need checking rather than treating the advertised land size as permission to build.</li>
          </ul>
          <h3 className="mt-8 font-serif text-2xl text-foreground">Selling a home after someone has died?</h3>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Bring the will, title details and any proposed sale dates. Our{" "}
            <Link to="/guides/$slug" params={{ slug: "how-to-apply-for-probate-in-queensland" }} className="font-semibold text-primary underline">Queensland probate application guide</Link>
            {" "}explains the grant process. For help with the estate itself, see our{" "}
            <Link to="/probate" className="font-semibold text-primary underline">Sunshine Coast probate and estate administration service</Link>.
            {" "}Do not assume a grant will arrive in time for a proposed settlement; check authority and timing before signing.
          </p>
          <p className="mt-8 text-sm text-muted-foreground">
            Local pages:{" "}
            <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="text-primary underline">Caloundra</Link>,{" "}
            <Link to="/conveyancing/$suburb" params={{ suburb: "golden-beach" }} className="text-primary underline">Golden Beach</Link>,{" "}
            <Link to="/conveyancing/$suburb" params={{ suburb: "baringa" }} className="text-primary underline">Baringa and Aura</Link>.{" "}
            Areas: {C.suburbs.join(", ")}. Also see{" "}
            <Link to="/conveyancing/$suburb" params={{ suburb: "redcliffe" }} className="text-primary underline">Redcliffe conveyancing</Link> and{" "}
            <Link to="/guides/$slug" params={{ slug: "cooling-off-period-queensland" }} className="text-primary underline">the Queensland cooling-off period</Link>.
          </p>
        </details>
      </section>
    </>
  );
}
